import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { 
  Search, 
  Sun, 
  Moon, 
  Menu, 
  X,
  ChevronDown,
  Users,
  Rocket,
  Calendar,
  BookOpen,
  MessageSquare,
  Briefcase,
  Mail,
  LogOut,
  Settings
} from 'lucide-react';
import { searchAPI } from '../../lib/api';

export const Navbar = () => {
  const { theme, toggleTheme } = useTheme();
  const { isAdmin, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showSearch, setShowSearch] = useState(false);

  const navLinks = [
    { 
      label: 'Communities', 
      href: '/communities',
      icon: Users,
      dropdown: [
        { label: 'All Communities', href: '/communities' },
        { label: 'AI & Machine Learning', href: '/communities?category=ai-ml' },
        { label: 'Robotics & Hardware', href: '/communities?category=robotics' },
        { label: 'Creative Tech', href: '/communities?category=creative' },
        { label: 'Startup Networks', href: '/communities?category=startup' },
        { label: 'Diversity Groups', href: '/communities?category=diversity' },
      ]
    },
    { 
      label: 'Startups', 
      href: '/startups',
      icon: Rocket,
      dropdown: [
        { label: 'Startup Directory', href: '/startups' },
        { label: 'Success Stories', href: '/success-stories' },
        { label: 'Coworking Spaces', href: '/coworking' },
        { label: 'Jobs', href: '/jobs' },
      ]
    },
    { 
      label: 'Events', 
      href: '/events',
      icon: Calendar,
      dropdown: [
        { label: 'All Events', href: '/events' },
        { label: 'Meetups', href: '/events?type=meetup' },
        { label: 'Conferences', href: '/events?type=conference' },
        { label: 'Hackathons', href: '/events?type=hackathon' },
      ]
    },
    { 
      label: 'Topics', 
      href: '/topics',
      icon: BookOpen,
    },
    { 
      label: 'Resources', 
      href: '/resources',
      icon: BookOpen,
    },
  ];

  const utilityLinks = [
    { label: 'Forums', href: '/forums', icon: MessageSquare },
    { label: 'Jobs', href: '/jobs', icon: Briefcase },
    { label: 'Newsletter', href: '/newsletter', icon: Mail },
  ];

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.length >= 2) {
      try {
        const res = await searchAPI.global(searchQuery);
        setSearchResults(res.data);
      } catch (err) {
        console.error('Search error:', err);
      }
    }
  };

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <header className="sticky top-0 z-50 glass border-b border-border" data-testid="navbar">
      <div className="container-wide">
        {/* Top utility bar */}
        <div className="hidden lg:flex items-center justify-end gap-6 py-2 border-b border-border text-xs">
          {utilityLinks.map(link => (
            <Link 
              key={link.href}
              to={link.href}
              className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
              data-testid={`utility-link-${link.label.toLowerCase()}`}
            >
              <link.icon className="w-3 h-3" />
              {link.label}
            </Link>
          ))}
          {isAdmin && (
            <Link 
              to="/admin"
              className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="admin-link"
            >
              <Settings className="w-3 h-3" />
              Admin
            </Link>
          )}
        </div>

        {/* Main navigation */}
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-8 h-8 bg-primary rounded-sm flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">PDX</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-bold text-sm tracking-tight">Portland Tech</span>
              <span className="text-xs text-muted-foreground block -mt-0.5">Ecosystem</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map(link => (
              link.dropdown ? (
                <DropdownMenu key={link.href}>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors rounded-sm ${
                        isActive(link.href) 
                          ? 'text-foreground bg-muted' 
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                      data-testid={`nav-${link.label.toLowerCase()}`}
                    >
                      {link.label}
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    {link.dropdown.map(item => (
                      <DropdownMenuItem key={item.href} asChild>
                        <Link to={item.href} className="w-full">
                          {item.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link 
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 text-sm font-medium transition-colors rounded-sm ${
                    isActive(link.href) 
                      ? 'text-foreground bg-muted' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              {showSearch ? (
                <form onSubmit={handleSearch} className="flex items-center">
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-48 h-9 text-sm"
                    autoFocus
                    data-testid="search-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowSearch(false);
                      setSearchQuery('');
                      setSearchResults(null);
                    }}
                    className="ml-1"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </form>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSearch(true)}
                  data-testid="search-toggle"
                >
                  <Search className="w-4 h-4" />
                </Button>
              )}

              {/* Search Results Dropdown */}
              {searchResults && showSearch && (
                <div className="absolute top-full right-0 mt-2 w-80 bg-card border border-border rounded-sm shadow-lg max-h-96 overflow-y-auto" data-testid="search-results">
                  {Object.entries(searchResults).map(([category, items]) => (
                    items.length > 0 && (
                      <div key={category} className="p-3 border-b border-border last:border-b-0">
                        <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">
                          {category}
                        </h4>
                        {items.map(item => (
                          <Link
                            key={item.id}
                            to={`/${category}/${item.id || item.slug}`}
                            className="block py-1.5 text-sm hover:text-primary transition-colors"
                            onClick={() => {
                              setShowSearch(false);
                              setSearchResults(null);
                            }}
                          >
                            {item.name || item.title}
                          </Link>
                        ))}
                      </div>
                    )
                  ))}
                  {Object.values(searchResults).every(arr => arr.length === 0) && (
                    <div className="p-4 text-sm text-muted-foreground text-center">
                      No results found
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? (
                <Sun className="w-4 h-4" />
              ) : (
                <Moon className="w-4 h-4" />
              )}
            </Button>

            {/* Auth - Only show when logged in */}
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="user-menu">
                    Admin
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/admin">Dashboard</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Mobile menu toggle */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-border bg-background" data-testid="mobile-menu">
          <nav className="container-wide py-4 space-y-1">
            {navLinks.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-sm ${
                  isActive(link.href)
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <link.icon className="w-4 h-4" />
                {link.label}
              </Link>
            ))}
            <div className="border-t border-border pt-2 mt-2">
              {utilityLinks.map(link => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="flex items-center gap-2 px-3 py-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground rounded-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <link.icon className="w-4 h-4" />
                  {link.label}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
